/**
 * AddressForm.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useMemo } from 'react';
import { CA_PROVINCES, resolveCountryCode, isDomesticOrPlaceholderCountryLabel } from '../utils/addressUtils';
import {
  getInternationalCountryOptions,
  isKnownInternationalCountry,
  countryNameToDomesticCode,
  OTHER_COUNTRY_VALUE,
} from '../utils/countries';
import { useLanguage } from '../context/LanguageContext';
import './AddressForm.css';

const US_STATES = [
  { name: 'Alabama', code: 'AL' }, { name: 'Alaska', code: 'AK' }, { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' }, { name: 'California', code: 'CA' }, { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' }, { name: 'Delaware', code: 'DE' },
  { name: 'District of Columbia', code: 'DC' }, { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' }, { name: 'Hawaii', code: 'HI' }, { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' }, { name: 'Indiana', code: 'IN' }, { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' }, { name: 'Kentucky', code: 'KY' }, { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' }, { name: 'Maryland', code: 'MD' }, { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' }, { name: 'Minnesota', code: 'MN' }, { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' }, { name: 'Montana', code: 'MT' }, { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' }, { name: 'New Hampshire', code: 'NH' }, { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' }, { name: 'New York', code: 'NY' }, { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' }, { name: 'Ohio', code: 'OH' }, { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' }, { name: 'Pennsylvania', code: 'PA' }, { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' }, { name: 'South Dakota', code: 'SD' }, { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' }, { name: 'Utah', code: 'UT' }, { name: 'Vermont', code: 'VT' },
  { name: 'Virginia', code: 'VA' }, { name: 'Washington', code: 'WA' }, { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' }, { name: 'Wyoming', code: 'WY' }
];

/**
 * AddressForm - Reusable address form component with dynamic international address support
 * 
 * @param {Object} address - Address data object
 * @param {Function} onChange - Callback function when address changes
 * @param {Boolean} disabled - Whether fields are disabled
 * @param {String} prefix - Optional prefix for field names (e.g., 'shipping')
 * @param {Object} errors - Object containing field error messages (e.g., { shippingStreet: 'Required' })
 */
const AddressForm = ({ address, onChange, disabled = false, prefix = '', errors = {} }) => {
  const { t } = useLanguage();
  const a = t.shop.address;
  const [countryType, setCountryType] = useState('CA'); // CA, US, or INTL

  // Keep dropdown in sync with countryCode first, then country label
  useEffect(() => {
    setCountryType(resolveCountryCode(address));
  }, [address.country, address.countryCode, address.countryName]);

  // Handle country selection change
  const handleCountryChange = (e) => {
    const value = e.target.value;
    setCountryType(value);

    // Update country name based on selection
    let countryName = '';
    if (value === 'CA') {
      countryName = 'Canada';
    } else if (value === 'US') {
      countryName = 'United States';
    } else if (value === 'INTL') {
      // Keep an existing international name; never carry over CA/US labels
      const existing = address.countryCode === 'INTL' ? (address.countryName || '') : '';
      countryName = existing && !isDomesticOrPlaceholderCountryLabel(existing) ? existing : '';
    }

    onChange({
      ...address,
      country: countryName,
      countryCode: value,
      province: '',
      state: '',
      region: '',
      countryName,
    });
  };

  const handleFieldChange = (field, value) => {
    const updates = { ...address, [field]: value };
    // Keep country label in sync for international free-text entry
    if (field === 'countryName') {
      updates.country = value;
    }
    onChange(updates);
  };

  const getFieldName = (field) => {
    return prefix ? `${prefix}${field.charAt(0).toUpperCase() + field.slice(1)}` : field;
  };

  const getErrorKey = (field) => {
    // Convert field name to error key format (e.g., 'street' -> 'shippingStreet')
    const fieldName = prefix ? `${prefix}${field.charAt(0).toUpperCase() + field.slice(1)}` : field;
    return fieldName;
  };

  const getFieldError = (field) => {
    const errorKey = getErrorKey(field);
    return errors[errorKey] || null;
  };

  const internationalCountryOptions = useMemo(() => getInternationalCountryOptions(), []);
  const intlCountryName = (address.countryName || '').trim();
  const intlUsesOtherCountry = intlCountryName && !isKnownInternationalCountry(intlCountryName);
  const intlCountrySelectValue = intlUsesOtherCountry
    ? OTHER_COUNTRY_VALUE
    : (intlCountryName || '');

  const handleInternationalCountryNameChange = (e) => {
    const value = e.target.value;

    if (value === OTHER_COUNTRY_VALUE) {
      onChange({
        ...address,
        countryName: '',
        country: '',
      });
      return;
    }

    if (!value) {
      onChange({
        ...address,
        countryName: '',
        country: '',
      });
      return;
    }

    const domesticCode = countryNameToDomesticCode(value);
    if (domesticCode) {
      handleCountryChange({ target: { value: domesticCode } });
      return;
    }

    onChange({
      ...address,
      countryName: value,
      country: value,
    });
  };

  const handleInternationalOtherCountryChange = (e) => {
    const value = e.target.value;
    onChange({
      ...address,
      countryName: value,
      country: value,
    });
  };

  return (
    <div className="address-form-container">
      {/* Country Selection */}
      <div className="form-group">
        <label htmlFor={`${prefix}country`}>{a.country}</label>
        <select
          id={`${prefix}country`}
          name={getFieldName('country')}
          value={countryType}
          onChange={handleCountryChange}
          disabled={disabled}
          className={`address-form-select ${getFieldError('countryName') ? 'error' : ''}`}
        >
          <option value="CA">{a.canada}</option>
          <option value="US">{a.unitedStates}</option>
          <option value="INTL">{a.international}</option>
        </select>
        {getFieldError('countryName') && (
          <span className="field-error">{getFieldError('countryName')}</span>
        )}
      </div>

      {/* Street Address */}
      <div className="form-group">
        <label htmlFor={`${prefix}street`}>
          {a.street} <span className="required">*</span>
        </label>
        <input
          type="text"
          id={`${prefix}street`}
          name={getFieldName('street')}
          value={address.street || address.addressLine1 || ''}
          onChange={(e) => handleFieldChange('street', e.target.value)}
          placeholder={a.streetPlaceholder}
          disabled={disabled}
          autoComplete="address-line1"
          className={getFieldError('street') ? 'error' : ''}
        />
        {getFieldError('street') && (
          <span className="field-error">{getFieldError('street')}</span>
        )}
      </div>

      {/* Street Address 2 */}
      <div className="form-group">
        <label htmlFor={`${prefix}street2`}>
          {a.line2} <span className="optional-label">{a.optional}</span>
        </label>
        <input
          type="text"
          id={`${prefix}street2`}
          name={getFieldName('street2')}
          value={address.street2 || address.addressLine2 || ''}
          onChange={(e) => handleFieldChange('street2', e.target.value)}
          placeholder={a.line2Placeholder}
          disabled={disabled}
          autoComplete="address-line2"
        />
      </div>

      {/* City and Province/State/Region Row */}
      <div className="form-row">
        {/* City - Always Visible */}
        <div className="form-group">
          <label htmlFor={`${prefix}city`}>
            {a.city} <span className="required">*</span>
          </label>
          <input
            type="text"
            id={`${prefix}city`}
            name={getFieldName('city')}
            value={address.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            placeholder={a.cityPlaceholder}
            disabled={disabled}
            autoComplete="address-level2"
            className={getFieldError('city') ? 'error' : ''}
          />
          {getFieldError('city') && (
            <span className="field-error">{getFieldError('city')}</span>
          )}
        </div>

        {/* Dynamic Province/State/Region */}
        <div className="form-group">
          {countryType === 'CA' && (
            <>
              <label htmlFor={`${prefix}province`}>
                {a.province} <span className="required">*</span>
              </label>
              <select
                id={`${prefix}province`}
                name={getFieldName('province')}
                value={address.province || ''}
                onChange={(e) => handleFieldChange('province', e.target.value)}
                disabled={disabled}
                className={`address-form-select ${getFieldError('province') ? 'error' : ''}`}
              >
                <option value="">Select...</option>
                {CA_PROVINCES.map((prov) => (
                  <option key={prov.code} value={prov.code}>
                    {prov.name}
                  </option>
                ))}
              </select>
              {getFieldError('province') && (
                <span className="field-error">{getFieldError('province')}</span>
              )}
              {!getFieldError('province') && (
                <span className="field-hint">Required for Canadian orders</span>
              )}
            </>
          )}

          {countryType === 'US' && (
            <>
              <label htmlFor={`${prefix}state`}>
                {a.state} <span className="required">*</span>
              </label>
              <select
                id={`${prefix}state`}
                name={getFieldName('state')}
                value={address.state || address.province || ''}
                onChange={(e) => handleFieldChange('state', e.target.value)}
                disabled={disabled}
                className={`address-form-select ${getFieldError('state') ? 'error' : ''}`}
              >
                <option value="">Select...</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
              {getFieldError('state') && (
                <span className="field-error">{getFieldError('state')}</span>
              )}
            </>
          )}

          {countryType === 'INTL' && (
            <>
              <label htmlFor={`${prefix}region`}>
                {a.region} <span className="required">*</span>
              </label>
              <input
                type="text"
                id={`${prefix}region`}
                name={getFieldName('region')}
                value={address.region || address.province || ''}
                onChange={(e) => handleFieldChange('region', e.target.value)}
                placeholder={a.regionPlaceholder}
                disabled={disabled}
                className={getFieldError('region') ? 'error' : ''}
              />
              {getFieldError('region') && (
                <span className="field-error">{getFieldError('region')}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Postal Code and Country Name Row */}
      <div className="form-row">
        {/* Postal/ZIP Code */}
        <div className="form-group">
          <label htmlFor={`${prefix}postalCode`}>
            {countryType === 'CA' && a.postalCA}
            {countryType === 'US' && a.postalUS}
            {countryType === 'INTL' && a.postalIntl}
            <span className="required">*</span>
          </label>
          <input
            type="text"
            id={`${prefix}postalCode`}
            name={getFieldName('postalCode')}
            value={address.postalCode || ''}
            onChange={(e) => handleFieldChange('postalCode', e.target.value)}
            placeholder={
              countryType === 'CA' ? 'A1A 1A1' :
                countryType === 'US' ? '12345' :
                  'e.g. SW1A 0AA'
            }
            disabled={disabled}
            autoComplete="postal-code"
            className={getFieldError('postalCode') ? 'error' : ''}
          />
          {getFieldError('postalCode') && (
            <span className="field-error">{getFieldError('postalCode')}</span>
          )}
        </div>

        {/* Country Name - International Only */}
        {countryType === 'INTL' && (
          <div className="form-group">
            <label htmlFor={`${prefix}countryName`}>
              {a.countryName} <span className="required">*</span>
            </label>
            <select
              id={`${prefix}countryName`}
              name={getFieldName('countryName')}
              value={intlCountrySelectValue}
              onChange={handleInternationalCountryNameChange}
              disabled={disabled}
              autoComplete="country-name"
              className={`address-form-select ${getFieldError('countryName') ? 'error' : ''}`}
            >
              <option value="">{a.selectCountry}</option>
              {internationalCountryOptions.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              ))}
              <option disabled>──────────</option>
              <option value={OTHER_COUNTRY_VALUE}>{a.otherNotListed}</option>
            </select>
            {(intlCountrySelectValue === OTHER_COUNTRY_VALUE || intlUsesOtherCountry) && (
              <input
                type="text"
                id={`${prefix}countryNameOther`}
                name={getFieldName('countryNameOther')}
                value={address.countryName || ''}
                onChange={handleInternationalOtherCountryChange}
                placeholder={a.otherCountry}
                disabled={disabled}
                className={`address-form-other-country ${getFieldError('countryName') ? 'error' : ''}`}
                aria-label={a.otherCountryAria}
              />
            )}
            {getFieldError('countryName') && (
              <span className="field-error">{getFieldError('countryName')}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressForm;

